import Slider from '@react-native-assets/slider';
import { useLocalSearchParams } from 'expo-router';
import React, { useEffect, useMemo, useState } from 'react';
import { View } from 'react-native';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import type { Extra } from '@/api';
import type { Discount as DiscountType } from '@/api/reservations/types';
import { useSubscriptionExtras } from '@/api/subscriptions/use-subscription-extras';
import {
  getCheapestModelKey,
  useSubscriptionOptions,
} from '@/api/subscriptions/use-subscription-options';
import { formatNumber, isInsuranceFree, useCurrentOrganization } from '@/core';
import mixpanelService from '@/core/clients/mixpanel';
import useSubscriptionStore from '@/stores/use-subscription-store';
import {
  GREY_10,
  GREY_30,
  GREY_40,
  GREY_60,
  GREY_90,
  PINKLING,
} from '@/style/colors';
import { Card, Text } from '@/ui';
import { AutopilotCardItem } from '@/ui/card-item/autopilot-card-item';
import { InsuranceCardItem } from '@/ui/card-item/insurance-card-item';
import { Discount } from '@/ui/discount';
import { Icon } from '@/ui/icon';
import { ShieldCheckmark } from '@/ui/icons/shield-checkmark';
import { ShieldSlash } from '@/ui/icons/shield-slash';
import { Loader } from '@/ui/loader';

const StepMarker = ({
  stepMarked,
  optionName,
}: {
  stepMarked: boolean;
  optionName: string;
}) => {
  return null;

  if (stepMarked) {
    return (
      <>
        <View
          style={{
            backgroundColor: PINKLING,
            paddingLeft: 8,
            paddingRight: 8,
            paddingTop: 4,
            paddingBottom: 4,
            position: 'absolute',
            width: 110,
            alignItems: 'center',
            borderRadius: 4,
            bottom: -8,
          }}
        >
          <Text
            type="bodyRegular"
            style={{
              color: UnistylesRuntime.themeName === 'dark' ? GREY_90 : GREY_10,
            }}
          >
            {optionName}
          </Text>

          <View
            style={{
              width: 0,
              height: 0,
              borderLeftWidth: 5,
              borderRightWidth: 5,
              borderBottomWidth: 0,
              borderTopWidth: 5,
              borderStyle: 'solid',
              borderLeftColor: 'transparent',
              borderRightColor: 'transparent',
              borderColor: PINKLING,
              position: 'absolute',
              bottom: -5,
              left: '50%',
              marginLeft: -5, // Half of the border width to center it
            }}
          />
        </View>

        <View
          style={{
            width: 1,
            height: 4,
            backgroundColor: GREY_40,
            position: 'absolute',
            top: 40,
          }}
        />
      </>
    );
  }

  return (
    <View
      style={{
        width: 1,
        height: 4,
        backgroundColor: GREY_40,
        position: 'absolute',
        top: 40,
      }}
    />
  );
};

export default function ChooseExtras({
  onAutopilotChange,
  onMileageChange,
}: {
  onAutopilotChange?: (autopilot: Extra) => void;
  onMileageChange?: (mileage: { name: string; amount: number }) => void;
}) {
  const { styles, theme } = useStyles(stylesheet);
  const local = useLocalSearchParams<{
    code?: string;
  }>();
  const [discountCode, setDiscountCode] = React.useState('');
  const setDiscount = useSubscriptionStore((state) => state.setDiscount);
  const autopilot = useSubscriptionStore((state) => state.autopilot);
  const setAutopilot = useSubscriptionStore((state) => state.setAutopilot);
  const insurance = useSubscriptionStore((state) => state.insurance);
  const setInsurance = useSubscriptionStore((state) => state.setInsurance);
  const period = useSubscriptionStore((state) => state.period);
  const [milageOptionIdx, setMilageOptionIdx] = useState(0);
  const setMileage = useSubscriptionStore((state) => state.setMileage);
  const mileage = useSubscriptionStore((state) => state.mileage);
  const setMileageCost = useSubscriptionStore((state) => state.setMileageCost);
  const brandName = useSubscriptionStore((state) => state.brandName);
  const modelName = useSubscriptionStore((state) => state.modelName);
  const trimName = useSubscriptionStore((state) => state.trimName);
  const address = useSubscriptionStore((state) => state.address);
  const startDatetime = useSubscriptionStore((state) => state.startDatetime);
  const car = useSubscriptionStore((state) => state.car);
  const { isEVFMC } = useCurrentOrganization();
  const [initialSliderIdx, setInitialSliderIdx] = useState(0);

  React.useEffect(() => {
    if (local.code) {
      setDiscountCode(local.code);
    }
  }, [local.code]);

  const { data: subscriptionOptions, isLoading: isLoadingOptions } =
    useSubscriptionOptions({
      variables: {
        address: address?.description,
        startDate: startDatetime!,
      },
      enabled: !!address && !!startDatetime,
    });

  const { data: subscriptionExtras, isLoading: isLoadingExtras } =
    useSubscriptionExtras({
      variables: {
        period: period === '6Months' ? '6_months' : period,
        carId: car!.id,
      },
      enabled: !!period && !!car,
    });

  useEffect(() => {
    if (!mileage && subscriptionExtras) {
      setMileage({
        name: subscriptionExtras.extraMileageOptions[0].name,
        amount: 0,
        costPerMile: subscriptionExtras.extraMileageOptions[0].costPerMile,
        unlimited: subscriptionExtras.extraMileageOptions[0].unlimited,
      });
    }
  }, [subscriptionExtras, mileage, setMileage, car]);

  useEffect(() => {
    if (!autopilot && subscriptionExtras) {
      const defaultAutopilot = subscriptionExtras.autopilotOptions.find(
        (option: any) => option.amount === 0
      );

      setAutopilot(defaultAutopilot as Extra);
    }
  }, [subscriptionExtras, autopilot, setAutopilot]);

  useEffect(() => {
    if (isEVFMC && subscriptionExtras && period === 'weekly') {
      const unlimitedIdx = subscriptionExtras.extraMileageOptions.findIndex(
        (option) => option.amount === -1
      );

      if (unlimitedIdx !== -1) {
        const unlimited = subscriptionExtras.extraMileageOptions[unlimitedIdx];

        setMilageOptionIdx(unlimitedIdx);
        setInitialSliderIdx(unlimitedIdx);

        setMileage({
          name: unlimited.name,
          amount: unlimited.amount,
          costPerMile: unlimited.costPerMile,
          unlimited: unlimited.unlimited,
        });

        onMileageChange?.({
          name: unlimited.name,
          amount: unlimited.amount,
        });

        return;
      }
    }
  }, [isEVFMC, subscriptionExtras, period]);

  const showMileage = useMemo(() => {
    if (isEVFMC) {
      return false;
    }

    return true;
  }, [isEVFMC]);

  const evfmcInsurance = isEVFMC
    ? subscriptionExtras?.insuranceOptions.find(
        (option) => !isInsuranceFree(option)
      )
    : undefined;
  const visibleInsuranceOptions = subscriptionExtras?.insuranceOptions ?? [];

  // EVFMC: default to the paid $16 insurance, matching the backend override,
  // unless the customer already explicitly chose No Protection (bring-your-own
  // insurance) — that choice should stick.
  useEffect(() => {
    if (
      evfmcInsurance &&
      insurance?.id !== evfmcInsurance.id &&
      !(insurance && isInsuranceFree(insurance))
    ) {
      setInsurance(evfmcInsurance);
    }
  }, [evfmcInsurance, insurance, setInsurance]);

  const carCost = useMemo(() => {
    if (!subscriptionOptions) return 0;

    const selectedPeriod = period ? subscriptionOptions[period] : undefined;
    if (!selectedPeriod) return 0;

    let cost = 0;

    const cheapestModelKey = getCheapestModelKey(selectedPeriod);

    if (period && cheapestModelKey && !modelName) {
      cost = selectedPeriod[cheapestModelKey].cheapestOption.cost;
    }

    if (period && modelName && !trimName) {
      cost =
        selectedPeriod[`${brandName}${modelName}`].cheapestOption.cost;
    }

    if (period && modelName && trimName) {
      cost =
        selectedPeriod[`${brandName}${modelName}`].trimChoices[trimName][0]
          .cost;
    }

    return period === '6Months' ? cost / 6 : cost;
  }, [subscriptionOptions, period, modelName, trimName]);

  const mileageCost = useMemo(() => {
    if (!subscriptionExtras) {
      return 0;
    }

    const m = subscriptionExtras.extraMileageOptions[milageOptionIdx];

    const baseMiles = subscriptionExtras?.extraMileageOptions[0].amount;

    const totalMiles = m.amount;

    const extraMiles = totalMiles - baseMiles;

    return m.costPerMile * extraMiles;
  }, [subscriptionExtras, milageOptionIdx]);

  useEffect(() => {
    setMileageCost(mileageCost);

    if (subscriptionExtras && mileage) {
      const idx = subscriptionExtras.extraMileageOptions.findIndex(
        (option) => option.name === mileage.name
      );

      setMilageOptionIdx(idx);

      if (initialSliderIdx === 0 && idx !== 0) {
        setInitialSliderIdx(idx);
      }
    }

    if (mileage) {
      setMileage({
        ...mileage,
        amount: mileageCost,
      });
    }
  }, [mileageCost]);

  if (isLoadingOptions || isLoadingExtras) {
    return <Loader />;
  }

  return (
    <View style={styles.container}>
      {brandName === 'tesla' ? (
        <>
          <View style={{ gap: 16 }}>
            <Text tx="subscriptions.autopilot" type="bodyBold" />

            <View style={styles.autopilotCardItemsContainer}>
              {subscriptionExtras &&
                subscriptionExtras.autopilotOptions.map((option) => (
                  <AutopilotCardItem
                    key={option.title}
                    title={option.title}
                    body={
                      option.title === 'Full Self-Driving Autopilot'
                        ? 'Vehicle recognizes stop signs and traffic lights. Note that FSD still requires hands on the wheel at all times.'
                        : ''
                    }
                    cost={
                      option.amount === 0 ? 'Included' : `$${option.amount}`
                    }
                    unit={
                      option.amount !== 0
                        ? `/ ${subscriptionOptions[period].unit}`
                        : ''
                    }
                    active={autopilot?.title === option.title}
                    onPress={() => {
                      setAutopilot(option);

                      onAutopilotChange?.(option);
                    }}
                    style={styles.autopilotCardItem}
                  />
                ))}
            </View>
          </View>
        </>
      ) : null}

      {showMileage ? (
        <View style={{ gap: 16, marginBottom: 16 }}>
          <View style={{ gap: 8 }}>
            <Text tx="subscriptions.chooseMileagePlan" type="bodyBold" />

            <Text
              type="caption1"
              style={{ color: theme.colors.text.secondary }}
            >
              Additional miles billed at $0.50 per mile
            </Text>
          </View>

          <Card>
            <View style={{ padding: 16 }}>
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                <Text type="title2">
                  {
                    subscriptionExtras?.extraMileageOptions[milageOptionIdx]
                      .name
                  }
                </Text>

                <View
                  style={{ flexDirection: 'row', gap: 4, alignItems: 'center' }}
                >
                  <Text type="title2">{`${
                    milageOptionIdx === 0
                      ? 'Included'
                      : `+$${formatNumber(mileageCost)}`
                  }`}</Text>
                  <Text
                    type="caption1"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    {milageOptionIdx === 0
                      ? ''
                      : ` / ${subscriptionOptions[period].unit}`}
                  </Text>
                </View>
              </View>

              <Slider
                style={styles.slider}
                minimumTrackTintColor={PINKLING}
                maximumTrackTintColor={
                  UnistylesRuntime.themeName === 'dark' ? GREY_60 : GREY_30
                }
                onValueChange={(val) => {
                  setMilageOptionIdx(val);

                  const m = subscriptionExtras.extraMileageOptions[val];

                  const baseMiles =
                    subscriptionExtras?.extraMileageOptions[0].amount;

                  const totalMiles = m.amount;

                  const extraMiles = totalMiles - baseMiles;

                  const cost = m.costPerMile * extraMiles;

                  setMileage({
                    name: m.name,
                    amount: m.amount,
                    costPerMile: cost,
                    unlimited: m.unlimited,
                  });

                  onMileageChange?.({
                    name: m.name,
                    amount: cost,
                  });
                }}
                minimumValue={0}
                maximumValue={Math.max(
                  0,
                  (subscriptionExtras?.extraMileageOptions.filter(
                    (option) => option.amount !== -1
                  ).length ?? 0) - 1
                )}
                value={initialSliderIdx}
                step={1}
                thumbTintColor={
                  UnistylesRuntime.themeName === 'dark' ? GREY_10 : GREY_60
                }
                tapToSeek
                StepMarker={({ stepMarked }) => (
                  <StepMarker
                    stepMarked={stepMarked}
                    optionName={
                      subscriptionExtras?.extraMileageOptions[milageOptionIdx]
                        .name
                    }
                  />
                )}
              />
            </View>
          </Card>
        </View>
      ) : null}

      <View style={{ gap: 16 }}>
        <Text tx="subscriptions.chooseYourProtection" type="bodyBold" />

        <View style={styles.insuranceCardItemsContainer}>
          {visibleInsuranceOptions.map((option) => (
            <InsuranceCardItem
              car={car}
              key={option.name}
              title={option.name}
              insurance={option}
              icon={
                <Icon
                  type="dressed"
                  icon={
                    option.name === 'No Protection' ? (
                      <ShieldSlash />
                    ) : (
                      <ShieldCheckmark />
                    )
                  }
                  fillColor={theme.colors.icon.color}
                  width={25}
                  height={25}
                />
              }
              body={
                <View>
                  {option.meta.descriptions.map(
                    (description: string, index: number) => (
                      <View
                        key={index}
                        style={{
                          flexDirection: 'row',
                          alignItems: 'flex-start',
                          marginBottom: 4,
                        }}
                      >
                        <Text
                          style={{
                            marginRight: 8,
                            color: theme.colors.text.secondary,
                          }}
                          type="bodyRegular"
                        >
                          •
                        </Text>
                        <Text
                          style={{
                            color: theme.colors.text.secondary,
                          }}
                          type="bodyRegular"
                        >
                          {description}
                        </Text>
                      </View>
                    )
                  )}
                </View>
              }
              footerBodyBold={
                option.name === 'No Protection'
                  ? 'Free'
                  : `+$${formatNumber(option.percentage * carCost)}`
              }
              footerBody={
                option.name !== 'No Protection' &&
                `/ ${subscriptionOptions[period].unit}`
              }
              active={insurance?.id === option.id}
              onPress={() => {
                setInsurance(option);
              }}
              style={styles.insuranceCardItem}
            />
          ))}
        </View>
      </View>

      <View style={{ gap: 16 }}>
        <Text tx="subscriptions.enterDiscountCode" type="bodyBold" />

        <View>
          <Discount
            title=""
            inputPlaceholder="Enter your discount code here"
            // inputLabel={t('discount.enterDiscountCodeLabel')}
            buttonTx={'discount.redeemCode'}
            onChangeText={(text: string) => {
              setDiscountCode(text);
            }}
            onPress={() => {}}
            onSuccess={(d: DiscountType) => {
              setDiscount(d);

              mixpanelService.track('Discount Code Redeemed', {
                Code: d.code,
                'Discount Amount': d.amount,
              });
            }}
            disabled={discountCode.length === 0}
            defaultText={local.code}
          />
        </View>
      </View>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    minWidth: '100%',
    paddingTop: 8,
    paddingBottom: 16,
    gap: 24,
  },
  autopilotCardItemsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  autopilotCardItem: {
    flexBasis: {
      xs: '100%',
      md: '49%',
    },
    maxWidth: {
      xs: '100%',
      md: '49%',
    },
    flexGrow: 1,
  },
  slider: {
    width: '100%',
    height: 40,
    alignSelf: 'center',
    marginTop: 30,
  },
  insuranceCardItemsContainer: {
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    gap: 16,
  },
  insuranceCardItem: {
    flexBasis: {
      md: '49%',
    },
    maxWidth: {
      md: '49%',
    },
    width: {
      xs: '100%',
      md: 'auto',
    },
  },
}));
